import { db, storage } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { Flavor } from '../types/flavor';

const COLLECTION_NAME = 'flavors';

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const flavorService = {
    async addFlavor(flavor: Flavor, iconFile?: File) {
        try {
            let iconUrl = '';
            if (iconFile) {
                const storageRef = ref(storage, `flavor-icons/${Date.now()}_${iconFile.name}`);
                const uploadResult = await uploadBytes(storageRef, iconFile);
                iconUrl = await getDownloadURL(uploadResult.ref);
            }

            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...flavor,
                iconUrl,
                createdAt: new Date(),
                updatedAt: new Date()
            });
            return { id: docRef.id, ...flavor, iconUrl };
        } catch (error) {
            throw new Error('Không thể thêm hương vị: ' + error);
        }
    },

    async updateFlavor(id: string, flavor: Partial<Flavor>, newIconFile?: File) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const currentDoc = await getDoc(docRef);

            if (!currentDoc.exists()) {
                throw new Error('Không tìm thấy hương vị');
            }

            let updateData = {
                ...flavor,
                updatedAt: new Date()
            };

            if (newIconFile) {
                // Delete old icon if exists
                const oldIconUrl = currentDoc.data().iconUrl;
                if (oldIconUrl && isFirebaseStorageUrl(oldIconUrl)) {
                    try {
                        const oldIconRef = ref(storage, oldIconUrl);
                        await deleteObject(oldIconRef);
                        console.log(`Successfully deleted old flavor icon: ${oldIconUrl}`);
                    } catch (error) {
                        console.error(`Error deleting old flavor icon ${oldIconUrl}:`, error);
                    }
                } else if (oldIconUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${oldIconUrl}`);
                }

                // Upload new icon
                const storageRef = ref(storage, `flavor-icons/${Date.now()}_${newIconFile.name}`);
                const uploadResult = await uploadBytes(storageRef, newIconFile);
                const newIconUrl = await getDownloadURL(uploadResult.ref);

                updateData = {
                    ...updateData,
                    iconUrl: newIconUrl
                };
            }

            await updateDoc(docRef, updateData);
            return { id, ...updateData };
        } catch (error) {
            throw new Error('Không thể cập nhật hương vị: ' + error);
        }
    },

    async deleteFlavor(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const iconUrl = data.iconUrl;
                
                // Xóa icon từ Firebase Storage nếu tồn tại
                if (iconUrl && isFirebaseStorageUrl(iconUrl)) {
                    try {
                        const iconRef = ref(storage, iconUrl);
                        await deleteObject(iconRef);
                        console.log(`Successfully deleted flavor icon: ${iconUrl}`);
                    } catch (error) {
                        console.error(`Error deleting flavor icon ${iconUrl}:`, error);
                        // Tiếp tục xử lý dù có lỗi khi xóa icon
                    }
                } else if (iconUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${iconUrl}`);
                }

                console.log(`Storage cleanup for flavor ID ${id}: ${iconUrl ? (isFirebaseStorageUrl(iconUrl) ? '1 icon deleted' : '1 non-Firebase URL skipped') : 'no icon to delete'}`);
            }

            // Xóa document từ Firestore
            await deleteDoc(docRef);
            console.log(`Successfully deleted flavor document with ID: ${id}`);
        } catch (error) {
            console.error(`Error deleting flavor with ID ${id}:`, error);
            throw new Error('Không thể xóa hương vị: ' + error);
        }
    },

    async getAllFlavors() {
        const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Flavor[];
    }
}; 